import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { Camera, X, ChevronDown, Minus, Plus, House } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { usePreferences } from '../../hooks/usePreferences'
import StoreInput from '../shared/StoreInput'
import { LOCATIONS, type LocationId, getDefaultLocation } from '../../constants/locations'
import BarcodeScanner from './BarcodeScanner'
import { track } from '../../lib/posthog'

// ─── Freshness category mapping ───────────────────────────────────────────────

function getFreshnessCategory(category: string): string {
  switch (category) {
    case 'produce':      return 'fresh_produce'
    case 'dairy':        return 'dairy'
    case 'meat':         return 'meat_fish'
    case 'bakery':       return 'bread_bakery'
    default:             return 'pantry_staple'
  }
}

const NO_EXPIRY_CATEGORIES = ['pantry', 'cleaning', 'personal_care']

// ─── Types ────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  'produce', 'dairy', 'meat', 'bakery', 'pantry', 'frozen', 'condiments', 'cleaning', 'personal_care',
] as const

export interface EditableItem {
  id:                       string
  name:                     string
  emoji?:                   string  // legacy column; no longer surfaced in UI
  display_category:         string

  price_paid?:              number | null
  track_expiration?:        boolean | null
  current_expiration_date?: string | null
}

interface Props {
  householdId: string
  onClose:     () => void
  onSuccess:   (itemName: string, isEdit: boolean) => void
  editItem?:   EditableItem
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split('T')[0]

function labelForCategory(cat: string): string {
  return cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

// ─── AddItemModal ─────────────────────────────────────────────────────────────

export default function AddItemModal({ householdId, onClose, onSuccess, editItem }: Props) {
  const queryClient = useQueryClient()
  const isEdit = !!editItem
  const { defaultTrackExpiration } = usePreferences()

  // ── Form state ───────────────────────────────────────────────────────────────

  const initialCategory = editItem?.display_category ?? 'pantry'

  const [itemName,        setItemName]        = useState(editItem?.name ?? '')
  const [category,        setCategory]        = useState(initialCategory)
  const [price,           setPrice]           = useState(editItem?.price_paid?.toString() ?? '')
  const [datePurchased,   setDatePurchased]   = useState(today())
  const [expirationDate,  setExpirationDate]  = useState(editItem?.current_expiration_date ?? '')
  const [trackExpiration, setTrackExpiration] = useState(() => {
    if (isEdit) return editItem?.track_expiration ?? true
    return defaultTrackExpiration && !NO_EXPIRY_CATEGORIES.includes(initialCategory)
  })
  const [storeName,   setStoreName]   = useState('')
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState('')
  const [showScanner, setShowScanner] = useState(false)
  const [location,    setLocation]    = useState<LocationId>(() =>
    getDefaultLocation(getFreshnessCategory(initialCategory))
  )

  // ── Quantity batch state (add mode only) ──────────────────────────────────────
  const [batchQty,    setBatchQty]    = useState(1)
  const [batchExpiry, setBatchExpiry] = useState('')

  // Auto-update location and track-expiration when category changes
  useEffect(() => {
    setLocation(getDefaultLocation(getFreshnessCategory(category)))
    if (!isEdit) {
      const noExpiry = NO_EXPIRY_CATEGORIES.includes(category)
      setTrackExpiration(!noExpiry)
    }
  }, [category]) // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = itemName.trim().length > 0 && (isEdit || datePurchased.length > 0) && !saving

  // ── Submit ───────────────────────────────────────────────────────────────────

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    setError('')

    try {
      const freshnessCategory = getFreshnessCategory(category)
      const parsedPrice       = parseFloat(price)
      const priceValue        = !isNaN(parsedPrice) && price.trim() !== '' ? parsedPrice : null

      if (isEdit && editItem) {
        // ── Update existing item ───────────────────────────────────────────────
        const { error: updateErr } = await supabase
          .from('pantry_items')
          .update({
            name:                    itemName.trim(),
            display_category:        category,
            freshness_category:      freshnessCategory,
            track_expiration:        trackExpiration,
            current_expiration_date: trackExpiration ? (expirationDate || null) : null,
          })
          .eq('id', editItem.id)

        if (updateErr) throw updateErr

        if (priceValue !== null) {
          const { data: recent } = await supabase
            .from('confirmations')
            .select('id')
            .eq('item_id', editItem.id)
            .order('confirmed_at', { ascending: false })
            .limit(1)
            .maybeSingle()

          if (recent) {
            await supabase
              .from('confirmations')
              .update({ price_paid: priceValue })
              .eq('id', recent.id)
          } else {
            await supabase
              .from('confirmations')
              .insert({
                item_id:      editItem.id,
                household_id: householdId,
                confirmed_at: new Date().toISOString(),
                source:       'manual',
                price_paid:   priceValue,
                quantity:     1,
              })
          }
        }

      } else {
        // ── Insert new item ────────────────────────────────────────────────────
        const confirmedAt     = new Date(datePurchased).toISOString()
        const effectiveExpiry = trackExpiration && expirationDate ? expirationDate : null

        const { data: newItem, error: insertErr } = await supabase
          .from('pantry_items')
          .insert({
            household_id:            householdId,
            name:                    itemName.trim(),
            display_category:        category,
            freshness_category:      freshnessCategory,
            last_confirmed_at:       confirmedAt,
            confirmation_count:      3,
            confidence_score:        0,
            is_active:               true,
            track_expiration:        trackExpiration,
            current_expiration_date: effectiveExpiry,
            location:                location,
          })
          .select()
          .single()

        if (insertErr) throw insertErr

        // Record initial confirmation
        if (priceValue !== null || effectiveExpiry || storeName.trim()) {
          await supabase.from('confirmations').insert({
            item_id:      newItem.id,
            household_id: householdId,
            confirmed_at: confirmedAt,
            source:       'manual',
            price_paid:   priceValue,
            store_name:   storeName.trim() || null,
            quantity:     1,
            ...(effectiveExpiry ? { expiration_date: effectiveExpiry } : {}),
          })
        }

        // Write store pricing data if both price and store are provided
        if (priceValue !== null && storeName.trim()) {
          await supabase.from('store_prices').insert({
            household_id: householdId,
            item_id:      newItem.id,
            store_name:   storeName.trim(),
            price:        priceValue,
            recorded_at:  new Date().toISOString(),
          })
          await supabase
            .from('user_stores')
            .upsert(
              { household_id: householdId, store_name: storeName.trim(), last_visited: new Date().toISOString().split('T')[0] },
              { onConflict: 'household_id,store_name' },
            )
          await supabase.rpc('increment_store_visit', {
            p_household_id: householdId,
            p_store_name:   storeName.trim(),
          })
        }

        // ── Save initial batch if quantity > 0 ─────────────────────────────────
        const batchExpiryFinal = batchExpiry || effectiveExpiry || null
        if (batchQty > 0) {
          await supabase.from('item_batches').insert({
            item_id:      newItem.id,
            household_id: householdId,
            quantity:     batchQty,
            expiry_date:  batchExpiryFinal,
            status:       'active',
          })
        }
      }

      queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
      // Only fire item_added on the create path — edits are a separate signal.
      if (!isEdit) track('item_added', { source: 'manual' })
      onSuccess(itemName.trim(), isEdit)
      onClose()

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end items-center">

      {showScanner && (
        <BarcodeScanner
          onResult={(name) => {
            setItemName(name)
            setShowScanner(false)
          }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onClose}
      />

      {/* Sheet */}
      <motion.div
        className="relative bg-cream rounded-t-[28px] shadow-2xl w-full max-w-[480px] max-h-[92dvh] overflow-y-auto"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >

        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="h-1 w-10 rounded-full bg-border-avatar" />
        </div>

        {/* Masthead */}
        <div className="flex items-start justify-between px-5 pt-2 pb-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
              {isEdit ? 'Edit' : 'Add to pantry'}
            </div>
            <h2 className="font-serif text-[22px] font-normal leading-[1.1] tracking-[-0.015em] text-ink">
              {isEdit ? (
                <>Edit <em className="italic">item</em>.</>
              ) : (
                <>Add an <em className="italic">item</em>.</>
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted"
          >
            <X className="h-[14px] w-[14px]" strokeWidth={1.8} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 pb-10 flex flex-col gap-5">

          {/* ── Field 1 — Item name ────────────────────────────────────────── */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted block mb-1.5">
              What is it?
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={itemName}
                onChange={e => setItemName(e.target.value)}
                placeholder="e.g. Greek yogurt"
                autoFocus
                required
                className="flex-1 rounded-[14px] border border-border-sand bg-paper px-4 py-3 font-serif text-[15px] text-ink placeholder:text-ink-muted placeholder:italic focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
              />
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                aria-label="Scan barcode"
                className="h-[50px] w-[50px] flex-shrink-0 rounded-[14px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted hover:bg-cream-warm transition"
              >
                <Camera className="h-[18px] w-[18px]" strokeWidth={1.7} />
              </button>
            </div>
          </div>

          {/* ── Field 2 — Category ────────────────────────────────────────── */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted block mb-1.5">
              Category
            </label>
            <div className="relative">
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full appearance-none rounded-[14px] border border-border-sand bg-paper px-4 py-3 pr-10 font-serif text-[15px] text-ink focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{labelForCategory(cat)}</option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted"
                strokeWidth={1.7}
              />
            </div>
          </div>

          {/* ── Field 3b — Storage location ──────────────────────────────── */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted block mb-1.5">
              Storage location
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(LOCATIONS).map(loc => {
                const isSelected = location === loc.id
                return (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() => setLocation(loc.id as LocationId)}
                    aria-pressed={isSelected}
                    className={`flex items-center gap-2 rounded-[14px] px-4 py-3 text-left transition-colors ${
                      isSelected
                        ? 'border-[1.5px] border-forest bg-ok-bg'
                        : 'border border-border-sand bg-paper'
                    }`}
                  >
                    <span className="text-[16px] flex-shrink-0">{loc.emoji}</span>
                    <span className={`font-serif text-[14px] ${isSelected ? 'font-medium text-forest' : 'text-ink'}`}>
                      {loc.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* ── Track expiration toggle ───────────────────────────────────── */}
          <div className="flex items-start justify-between gap-3 py-1">
            <div className="flex-1 min-w-0">
              <div className="font-serif text-[14px] font-medium text-ink">Track expiration date</div>
              <div className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.4]">
                Turn off for pasta, canned goods, cleaning supplies
              </div>
            </div>
            <button
              type="button"
              onClick={() => setTrackExpiration(v => !v)}
              aria-pressed={trackExpiration}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors mt-0.5 ${
                trackExpiration ? 'bg-forest' : 'bg-border-avatar'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 rounded-full bg-paper shadow-sm transition-transform duration-200 ${
                  trackExpiration ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* ── Add-mode-only fields ──────────────────────────────────────── */}
          {!isEdit && (
            <>
              {/* Field 4 — Expiration date (only if tracking) */}
              {trackExpiration && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted block mb-1.5">
                    Expiration date <span className="normal-case tracking-normal font-serif italic text-ink-muted/70">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={e => setExpirationDate(e.target.value)}
                    min={today()}
                    max="2099-12-31"
                    className="w-full rounded-[14px] border border-border-sand bg-paper px-4 py-3 font-serif text-[15px] text-ink focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
                  />
                  <p className="font-serif text-[11px] italic text-ink-muted mt-1.5 leading-[1.4]">
                    We'll remind you before it expires.
                  </p>
                </div>
              )}

              {/* Field 5 — Date purchased */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted block mb-1.5">
                  When did you buy it?
                </label>
                <input
                  type="date"
                  value={datePurchased}
                  onChange={e => setDatePurchased(e.target.value)}
                  max={today()}
                  required
                  className="w-full rounded-[14px] border border-border-sand bg-paper px-4 py-3 font-serif text-[15px] text-ink focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
                />
              </div>

              {/* Quantity at home — nested card */}
              <div className="rounded-[16px] border border-border-sand bg-cream-warm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <House className="h-[14px] w-[14px] text-terracotta" strokeWidth={1.7} />
                  <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta">
                    How many at home?
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => setBatchQty(q => Math.max(0, q - 1))}
                    aria-label="Decrease"
                    disabled={batchQty === 0}
                    className="h-10 w-10 flex-shrink-0 rounded-full border border-border-sand bg-paper flex items-center justify-center text-ink disabled:opacity-40"
                  >
                    <Minus className="h-[14px] w-[14px]" strokeWidth={2} />
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={batchQty}
                    onChange={e => setBatchQty(Math.max(0, parseInt(e.target.value) || 0))}
                    className="flex-1 rounded-[12px] border border-border-sand bg-paper py-2.5 text-center font-serif text-[20px] font-medium text-ink focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setBatchQty(q => q + 1)}
                    aria-label="Increase"
                    className="h-10 w-10 flex-shrink-0 rounded-full bg-forest text-forest-ink flex items-center justify-center"
                  >
                    <Plus className="h-[14px] w-[14px]" strokeWidth={2} />
                  </button>
                </div>

                {batchQty > 0 && (
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-ink-muted block mb-1.5">
                      Expiry for this batch <span className="normal-case tracking-normal font-serif italic">(optional)</span>
                    </label>
                    <input
                      type="date"
                      value={batchExpiry}
                      onChange={e => setBatchExpiry(e.target.value)}
                      min={today()}
                      className="w-full rounded-[12px] border border-border-sand bg-paper px-4 py-2.5 font-serif text-[14px] text-ink focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
                    />
                    <p className="font-serif text-[11px] italic text-ink-muted mt-1.5 leading-[1.4]">
                      You can add more batches with different expiry dates later.
                    </p>
                  </div>
                )}

                {batchQty === 0 && (
                  <p className="font-serif text-[11px] italic text-ink-muted leading-[1.4]">
                    Set to 0 if you're just tracking this item for future purchases.
                  </p>
                )}
              </div>
            </>
          )}

          {/* ── Field 6 — Price paid (both modes) ────────────────────────── */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted block mb-1.5">
              Price paid <span className="normal-case tracking-normal font-serif italic text-ink-muted/70">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 font-serif text-[15px] text-ink-muted">
                $
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={price}
                onChange={e => setPrice(e.target.value)}
                className="w-full rounded-[14px] border border-border-sand bg-paper pl-8 pr-4 py-3 font-serif text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
              />
            </div>
          </div>

          {/* ── Field 6b — Store ─────────────────────────────────────────── */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted block mb-1.5">
              Where did you buy it? <span className="normal-case tracking-normal font-serif italic text-ink-muted/70">(optional)</span>
            </label>
            <StoreInput
              value={storeName}
              onChange={setStoreName}
              householdId={householdId}
              placeholder="e.g. Trader Joe's"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-[12px] bg-critical-bg border border-critical/20 px-4 py-3 font-serif text-[13px] italic text-critical text-center">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-[14px] bg-forest text-forest-ink py-[14px] text-[14px] font-bold tracking-[-0.01em] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving
              ? isEdit ? 'Saving…' : 'Adding…'
              : isEdit ? 'Save changes' : 'Add to my pantry'}
          </button>

        </form>
      </motion.div>
    </div>
  )
}
